import { useCallback, type ReactNode } from 'react';

import { useAuth } from '@repo/auth';

import { ConnectedAccessDenied } from './connected-access-denied';
import { ProtectedRoute as BaseProtectedRoute } from './protected-route';
import { useRouterAdapter } from './router-adapter';

export interface ConnectedProtectedRouteProps {
  children: ReactNode;
  /**
   * Required roles for this route.
   * User must have AT LEAST ONE of the listed roles to access.
   */
  requiredRoles?: string[];
  /**
   * Required permissions for this route.
   * User must have ALL listed permissions to access.
   */
  requiredPermissions?: string[];
  /**
   * Custom fallback component while checking auth
   */
  loadingFallback?: ReactNode;
  /**
   * Whether to show role information on access denied (default: false).
   * Useful for debugging in tenant-facing apps.
   */
  showRoleInfo?: boolean;
}

/**
 * Connected Protected Route Component
 *
 * A connected version of ProtectedRoute that automatically wires up
 * the router (for login redirect) and auth (for authentication state).
 *
 * Uses ConnectedAccessDenied as the default access denied fallback.
 * This eliminates the need for each app to create its own wrapper.
 *
 * @example
 * ```tsx
 * // In a layout or page
 * <ConnectedProtectedRoute>
 *   <MyProtectedContent />
 * </ConnectedProtectedRoute>
 *
 * // With required roles
 * <ConnectedProtectedRoute requiredRoles={['admin']}>
 *   <AdminPanel />
 * </ConnectedProtectedRoute>
 *
 * // With role info shown on access denied
 * <ConnectedProtectedRoute requiredRoles={['tenant-owner']} showRoleInfo>
 *   <TenantDashboard />
 * </ConnectedProtectedRoute>
 * ```
 */
export function ConnectedProtectedRoute({
  children,
  requiredRoles,
  requiredPermissions,
  loadingFallback,
  showRoleInfo = false,
}: ConnectedProtectedRouteProps) {
  const { pathname, replace } = useRouterAdapter();
  const { isAuthenticated, isLoading, roles } = useAuth();

  const handleLoginRedirect = useCallback(() => {
    const returnTo = encodeURIComponent(pathname);
    replace(`/auth/login?returnTo=${returnTo}`);
  }, [pathname, replace]);

  // TODO: Check required permissions when permission system is implemented
  if (requiredPermissions && requiredPermissions.length > 0) {
    console.log(
      '[ConnectedProtectedRoute] Permission check not yet implemented',
    );
  }

  return (
    <BaseProtectedRoute
      isLoading={isLoading}
      isAuthenticated={isAuthenticated}
      roles={roles}
      requiredRoles={requiredRoles}
      loadingFallback={loadingFallback}
      accessDeniedFallback={
        <ConnectedAccessDenied
          requiredRoles={requiredRoles}
          showRoleInfo={showRoleInfo}
        />
      }
      onLoginRedirect={handleLoginRedirect}
    >
      {children}
    </BaseProtectedRoute>
  );
}

/**
 * Hook to check if current user has a specific permission
 */
export function useHasPermission(_permission: string): boolean {
  // TODO: Implement permission checking when permissions are loaded
  return true;
}

/**
 * Hook to check if current user has all of the specified permissions
 */
export function useHasAllPermissions(_permissions: string[]): boolean {
  // TODO: Implement permission checking when permissions are loaded
  return true;
}

/**
 * Hook to check if current user has any of the specified permissions
 */
export function useHasAnyPermission(_permissions: string[]): boolean {
  // TODO: Implement permission checking when permissions are loaded
  return true;
}
